import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  Divider,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
} from '@mui/material';
import { 
  PhoneForwarded as PhoneForwardedIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { api } from '../services/api';
import {
  formatLanguage,
  formatLatencyMs,
  formatTranslationStatus,
  getTranslationStatusColor,
} from '../utils/translationUi';

const formatDisplayValue = (value, fallback = '') => {
  if (value === null || value === undefined || value === '') {
    return fallback;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  if (typeof value === 'object') {
    return value.message || value.error || JSON.stringify(value);
  }

  return String(value);
};

const formatBytes = (bytes) => {
  const numericBytes = Number(bytes || 0);
  if (!Number.isFinite(numericBytes) || numericBytes <= 0) {
    return '0 bytes';
  }

  if (numericBytes >= 1024 * 1024) {
    return `${(numericBytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  if (numericBytes >= 1024) {
    return `${Math.round(numericBytes / 1024)} KB`;
  }

  return `${numericBytes} bytes`;
};

const getAudioMimeType = (file) => {
  if (file?.type) {
    return file.type;
  }

  const name = String(file?.name || '').toLowerCase();
  if (name.endsWith('.mp3')) {
    return 'audio/mpeg';
  }
  if (name.endsWith('.wav') || name.endsWith('.wave')) {
    return 'audio/wav';
  }

  return '';
};

const readAudioMetadata = async (file) => {
  const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextCtor) {
    return {};
  }

  const audioContext = new AudioContextCtor();
  try {
    const decoded = await audioContext.decodeAudioData(await file.arrayBuffer());
    return {
      durationMs: Math.round(decoded.duration * 1000),
      sampleRateHertz: decoded.sampleRate,
      audioChannelCount: decoded.numberOfChannels,
    };
  } finally {
    if (typeof audioContext.close === 'function') {
      await audioContext.close();
    }
  }
};

const getErrorMessage = (error) => {
  const data = error?.response?.data;

  if (data?.error) {
    return formatDisplayValue(data.error, 'Failed to place call');
  }

  if (data?.message) {
    return formatDisplayValue(data.message, 'Failed to place call');
  }

  return formatDisplayValue(error?.message, 'Failed to place call');
};

const formatTraceAction = (action) => {
  if (action === 'CALL_SETUP_REQUEST') {
    return 'CALL SETUP REQUEST';
  }

  return String(action || 'EVENT').replace(/_/g, ' ');
};

export default function SipSimulator() {
  const [callerNumber, setCallerNumber] = useState('+66814969080');
  const [calleeNumber, setCalleeNumber] = useState('6070');
  const [serviceName, setServiceName] = useState('default');
  const [callerLanguage, setCallerLanguage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState({ open: false, severity: 'success', message: '' });
  const [callLogs, setCallLogs] = useState([]);
  const [expandedLog, setExpandedLog] = useState(null);
  const [simulatorStatus, setSimulatorStatus] = useState(null);
  const [scenarios, setScenarios] = useState([]);
  const [audioFixtures, setAudioFixtures] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState('ALLOW_CALL');
  const [scenarioProviderMode, setScenarioProviderMode] = useState('fixture');
  const [scenarioAudioFixtureId, setScenarioAudioFixtureId] = useState('');
  const [uploadedScenarioAudio, setUploadedScenarioAudio] = useState(null);
  const [audioUploadLabel, setAudioUploadLabel] = useState('');
  const [scenarioLimits, setScenarioLimits] = useState({
    maxAudioBytes: 5 * 1024 * 1024,
    allowedAudioMimeTypes: ['audio/wav', 'audio/wave', 'audio/x-wav', 'audio/mpeg', 'audio/mp3'],
  });

  const handleClose = () => setNotice((prev) => ({ ...prev, open: false }));

  useEffect(() => {
    let isMounted = true;

    api.getSipSimulatorStatus()
      .then((status) => {
        if (isMounted) {
          setSimulatorStatus(status);
        }
      })
      .catch(() => {
        if (isMounted) {
          setSimulatorStatus(null);
        }
      });

    api.getSipSimulatorScenarios()
      .then((response) => {
        if (isMounted) {
          const items = response.items || [];
          setScenarios(items);
          setAudioFixtures(response.audioFixtures || []);
          setScenarioLimits((prev) => ({
            ...prev,
            ...(response.limits || {}),
          }));
          const defaultScenario = items.find((item) => item.id === 'ALLOW_CALL') || items[0];
          if (defaultScenario) {
            setSelectedScenarioId(defaultScenario.id);
            setScenarioProviderMode(defaultScenario.defaults?.providerMode || 'fixture');
            setScenarioAudioFixtureId(defaultScenario.defaults?.audioFixtureId || '');
          }
        }
      })
      .catch(() => {
        if (isMounted) {
          setScenarios([]);
          setAudioFixtures([]);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const copyRhinoTasTrace = async (log) => {
    const payload = log.rhinoTrace?.trace || [];
    const jsonText = JSON.stringify(payload, null, 2);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(jsonText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = jsonText;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }

      setNotice({ open: true, severity: 'success', message: 'Rhino TAS trace copied to clipboard.' });
    } catch (error) {
      setNotice({ open: true, severity: 'error', message: 'Failed to copy Rhino TAS trace.' });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircleIcon color="success" fontSize="small" />;
      case 'error':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      case 'processing':
        return <ScheduleIcon color="info" fontSize="small" />;
      default:
        return <CheckCircleIcon color="action" fontSize="small" />;
    }
  };

  const handleCall = async () => {
    setSubmitting(true);
    const timestamp = new Date();
    try {
      const response = await api.placeSipCall({ callerNumber, calleeNumber, serviceName, callerLanguage });
      const newLog = {
        id: `${timestamp.getTime()}-${callerNumber}-${calleeNumber}`,
        callerNumber,
        calleeNumber,
        status: 'success',
        message: 'Call placed successfully',
        timestamp,
        trace: response.trace || [],
        rhinoTrace: response.rhinoTrace || null,
        scammerCheck: response.scammerCheck,
        translationSession: response.translationSession || null,
        action: response.action,
        duration: response.duration,
      };
      setCallLogs((prev) => [newLog, ...prev].slice(0, 20));
      
      setNotice({
        open: true,
        severity: response.scammerCheck?.isScammer ? 'warning' : 'success',
        message: response.scammerCheck?.isScammer 
          ? `⚠️ Scammer detected: ${response.action} action recommended`
          : `✓ Call initiated: ${callerNumber} -> ${calleeNumber}`
      });
    } catch (error) {
      const message = getErrorMessage(error);
      const errorResponse = error?.response?.data || {};
      setCallLogs((prev) => [
        {
          id: `${timestamp.getTime()}-${callerNumber}-${calleeNumber}`,
          callerNumber,
          calleeNumber,
          status: 'error',
          message,
          timestamp,
          trace: errorResponse.trace || [],
          rhinoTrace: errorResponse.rhinoTrace || null,
          translationSession: errorResponse.translationSession || null,
          action: errorResponse.action,
          duration: errorResponse.duration,
        },
        ...prev,
      ].slice(0, 20));
      setNotice({ open: true, severity: 'error', message });
    } finally {
      setSubmitting(false);
    }
  };

  const toggleExpand = (logId) => {
    setExpandedLog(expandedLog === logId ? null : logId);
  };

  const getRhinoSipLegTrace = (log) => {
    const rhinoEvents = (log.rhinoTrace?.trace || [])
      .filter((evt) => ['INCOMING_SIP_EVENT', 'B2BUA_ACTION', 'SIP_HEADER_MOD', 'B2BUA_STATE_CHANGE', 'CALL_COMPLETION'].includes(evt.event))
      .map((evt) => {
        let direction = 'Rhino ↔ SIP Server';
        let sipMsg = '';
        
        if (evt.event === 'INCOMING_SIP_EVENT') {
          direction = 'SIP Server → Rhino';
          sipMsg = `${evt.method || 'SIP'} ${evt.from || ''} → ${evt.to || ''}`.trim();
        } else if (evt.event === 'B2BUA_STATE_CHANGE') {
          sipMsg = `Leg: ${evt.leg || 'AB'} | State: ${evt.state || 'UNKNOWN'}`;
        }

        return {
          source: 'rhinoTrace',
          time: typeof evt.timestamp === 'number' ? evt.timestamp : null,
          direction,
          protocol: evt.method ? `SIP ${evt.method}` : 'SIP/B2BUA',
          event: evt.event,
          sipMessage: sipMsg || evt.action || evt.description || evt.state || evt.reason || '',
          details: evt.action || evt.description || evt.state || evt.reason || 'Event logged'
        };
      });

    const simEvents = (log.trace || [])
      .filter((step) => ['CALL_INITIATED', 'CALL_SETUP_REQUEST', 'CALL_COMPLETED'].includes(step.action))
      .map((step) => ({
        source: 'simTrace',
        time: typeof step.timestamp === 'number' ? step.timestamp : null,
        direction: step.action === 'CALL_INITIATED' ? 'Web UI → API Backend' :
                   step.action === 'CALL_SETUP_REQUEST' ? 'API → Simulator Gateway' :
                   'Simulator Gateway → API',
        protocol: step.action === 'CALL_SETUP_REQUEST' ? 'Call setup' : 'SIP',
        event: step.action,
        sipMessage: step.action === 'CALL_SETUP_REQUEST' ? (step.details?.fromNumber || '') + ' → ' + (step.details?.toNumber || '') : '',
        details: step.message || 'Event logged'
      }));

    const allEvents = [...simEvents, ...rhinoEvents].sort((a, b) => {
      const timeA = a.time ?? Number.MAX_SAFE_INTEGER;
      const timeB = b.time ?? Number.MAX_SAFE_INTEGER;
      return timeA - timeB;
    });

    // Find baselines: the first sim event and first rhino event
    const firstSimEvent = allEvents.find(e => e.source === 'simTrace' && e.time !== null);
    const firstRhinoEvent = allEvents.find(e => e.source === 'rhinoTrace' && e.time !== null);

    // Calculate the offset between the two sources
    // If sim events have small values (relative) and rhino events have large values (absolute),
    // align them by using the rhino first event as reference and applying sim events relative offset
    let baseTime = 0;
    if (firstSimEvent && firstRhinoEvent) {
      // Check if there's a scale mismatch (sim is relative, rhino is absolute)
      if (firstSimEvent.time < 1000 && firstRhinoEvent.time > 1000000) {
        // Sim events are relative, rhino events are absolute
        // Use rhino's first event as the reference point
        baseTime = firstRhinoEvent.time - firstSimEvent.time;
      } else {
        // Both are on same scale, use first event overall
        baseTime = allEvents[0]?.time ?? 0;
      }
    } else if (firstRhinoEvent) {
      baseTime = firstRhinoEvent.time;
    } else if (firstSimEvent) {
      baseTime = firstSimEvent.time;
    }

    // Calculate relative time offsets, handling mismatched bases
    return allEvents.map((evt) => ({
      ...evt,
      relativeTime: evt.time !== null 
        ? (evt.source === 'simTrace' && firstRhinoEvent && firstSimEvent && firstSimEvent.time < 1000 && firstRhinoEvent.time > 1000000
            ? evt.time - firstSimEvent.time  // For sim events, use their own relative offset
            : evt.time - baseTime)            // For rhino events or same-scale, use base offset
        : null
    }));
  };

  const formatRhinoEventDetails = (evt) => {
    const parts = [];
    if (evt.message) parts.push(evt.message);
    if (evt.description) parts.push(evt.description);
    if (evt.method) parts.push(`Method: ${evt.method}`);
    if (evt.action) parts.push(`Action: ${evt.action}`);
    if (evt.decision) parts.push(`Decision: ${evt.decision}`);
    if (evt.state) parts.push(`State: ${evt.state}`);
    if (evt.leg) parts.push(`Leg: ${evt.leg}`);
    if (evt.reason) parts.push(`Reason: ${evt.reason}`);
    if (evt.endpoint) parts.push(`Endpoint: ${evt.endpoint}`);
    if (evt.policy) parts.push(`Policy: ${evt.policy}`);
    if (typeof evt.statusCode === 'number') parts.push(`Status: ${evt.statusCode}`);
    if (typeof evt.durationMs === 'number') parts.push(`Duration: ${evt.durationMs}ms`);
    if (evt.from || evt.to) parts.push(`From: ${evt.from || '-'} To: ${evt.to || '-'}`);
    if (typeof evt.callDurationSeconds === 'number') parts.push(`Call: ${evt.callDurationSeconds}s`);
    if (typeof evt.tracingDurationMs === 'number') parts.push(`Trace: ${evt.tracingDurationMs}ms`);
    return parts.join(' | ') || 'Event logged';
  };

  const formatRhinoEventPayload = (evt) => {
    const payload = evt.metadata || evt.data || evt.result || evt.finalData || evt.headers;
    if (!payload) return '';
    try {
      return JSON.stringify(payload);
    } catch (error) {
      return '';
    }
  };

  const getRhinoTasTrace = (log) => {
    const events = log.rhinoTrace?.trace || [];
    const firstTime = events.find((evt) => typeof evt.timestamp === 'number')?.timestamp ?? null;
    return events.map((evt, index) => ({
      id: `${log.id}-rhino-${index}`,
      time: typeof evt.timestamp === 'number' && firstTime !== null ? evt.timestamp - firstTime : null,
      event: evt.event || 'UNKNOWN',
      status: evt.status || 'INFO',
      details: formatRhinoEventDetails(evt),
      payload: formatRhinoEventPayload(evt)
    }));
  };

  const selectedScenario = scenarios.find((scenario) => scenario.id === selectedScenarioId);

  const handleScenarioChange = (event) => {
    const nextId = event.target.value;
    const nextScenario = scenarios.find((scenario) => scenario.id === nextId);
    setSelectedScenarioId(nextId);
    if (nextScenario?.defaults) {
      setCallerNumber(nextScenario.defaults.callerNumber || callerNumber);
      setCalleeNumber(nextScenario.defaults.calleeNumber || calleeNumber);
      setCallerLanguage('');
      setScenarioProviderMode(nextScenario.defaults.providerMode || 'fixture');
      setScenarioAudioFixtureId(nextScenario.defaults.audioFixtureId || '');
      setUploadedScenarioAudio(null);
      setAudioUploadLabel('');
    }
  };

  const handleScenarioAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    const mimeType = getAudioMimeType(file);
    const allowedTypes = scenarioLimits.allowedAudioMimeTypes || [];
    if (allowedTypes.length > 0 && mimeType && !allowedTypes.includes(mimeType)) {
      setNotice({ open: true, severity: 'error', message: 'Only WAV or MP3 audio fixtures are supported.' });
      event.target.value = '';
      return;
    }

    if (scenarioLimits.maxAudioBytes && file.size > scenarioLimits.maxAudioBytes) {
      setNotice({
        open: true,
        severity: 'error',
        message: `Audio fixture is too large. Use a file up to ${formatBytes(scenarioLimits.maxAudioBytes)}.`
      });
      event.target.value = '';
      return;
    }

    try {
      const [audioContentBase64, audioMetadata] = await Promise.all([
        new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => {
            const result = String(reader.result || '');
            resolve(result.includes(',') ? result.split(',').pop() : result);
          };
          reader.onerror = () => reject(reader.error || new Error('Audio file read failed'));
          reader.readAsDataURL(file);
        }),
        readAudioMetadata(file).catch(() => ({})),
      ]);

      const uploaded = await api.uploadSipScenarioAudio({
        fileName: file.name,
        mimeType: mimeType || 'audio/wav',
        audioContentBase64,
        ...audioMetadata,
      });

      setUploadedScenarioAudio(uploaded);
      setAudioUploadLabel(
        `${file.name} (${uploaded.audioBytes} bytes${uploaded.durationMs ? `, ${Math.round(uploaded.durationMs / 1000)}s` : ''})`
      );
      setScenarioAudioFixtureId('');
      setScenarioProviderMode('configured');
      setNotice({ open: true, severity: 'success', message: 'Audio fixture loaded for this scenario run.' });
    } catch (error) {
      setNotice({ open: true, severity: 'error', message: getErrorMessage(error) });
    } finally {
      event.target.value = '';
    }
  };

  const handleRunScenario = async () => {
    if (!selectedScenarioId) {
      setNotice({ open: true, severity: 'error', message: 'Select a simulator scenario first.' });
      return;
    }

    setSubmitting(true);
    const timestamp = new Date();

    try {
      const response = await api.runSipScenario({
        scenarioId: selectedScenarioId,
        callerNumber,
        calleeNumber,
        serviceId: serviceName,
        sourceLanguage: callerLanguage || null,
        targetLanguage: selectedScenario?.defaults?.targetLanguage || 'th-TH',
        providerMode: scenarioProviderMode,
        audioFixtureId: scenarioAudioFixtureId || undefined,
        uploadedAudio: uploadedScenarioAudio || undefined
      });

      const scenarioTranslation = response.translationScenario
        ? {
            status: response.translationScenario.status,
            skipReason: response.translationScenario.skipReason || null,
            detectedSourceLanguage: response.translationScenario.detectedSourceLanguage,
            targetLanguage: response.translationScenario.targetLanguage,
            audioMode: 'SCENARIO_TEST',
            latency: response.translationScenario.latency || {},
            metadata: {
              providerMode: response.translationScenario.providerMode
            }
          }
        : null;

      const newLog = {
        id: response.callSessionId || `${timestamp.getTime()}-${selectedScenarioId}`,
        callerNumber,
        calleeNumber,
        status: 'success',
        message: `${selectedScenario?.label || selectedScenarioId} completed`,
        timestamp,
        trace: response.trace || [],
        rhinoTrace: response.rhinoTrace || null,
        scammerCheck: response.scammerCheck,
        translationSession: scenarioTranslation,
        scenarioId: response.scenarioId,
        scenarioArtifact: response.artifact || null,
        expected: response.expected,
        action: response.action,
        duration: response.duration,
      };

      setCallLogs((prev) => [newLog, ...prev].slice(0, 20));
      setExpandedLog(newLog.id);
      setNotice({ open: true, severity: 'success', message: `${selectedScenario?.label || 'Scenario'} completed.` });
    } catch (error) {
      setNotice({ open: true, severity: 'error', message: getErrorMessage(error) });
    } finally {
      setSubmitting(false);
    }
  };

  const handlePrimaryTestCall = () => {
    if (scenarios.length > 0 && selectedScenarioId) {
      return handleRunScenario();
    }

    return handleCall();
  };

  const renderTranslationDetails = (session) => {
    if (!session) {
      return null;
    }

    const latency = session.latency || {};
    const metadata = session.metadata || {};

    return (
      <Box sx={{
        mt: 2,
        p: 2,
        bgcolor: '#f7fbff',
        border: '1px solid #cfe3ff',
        borderRadius: 1,
        color: '#111',
      }}>
        <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111' }}>
            Translation Status
          </Typography>
          <Chip
            label={formatTranslationStatus(session.status)}
            color={getTranslationStatusColor(session.status)}
            size="small"
          />
          {session.skipReason && (
            <Chip label={formatTranslationStatus(session.skipReason)} color="warning" size="small" variant="outlined" />
          )}
        </Box>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', md: 'repeat(4, minmax(0, 1fr))' },
          gap: 1,
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary">Detected language</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatLanguage(session.detectedSourceLanguage)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Target language</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatLanguage(session.targetLanguage)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Audio mode</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatTranslationStatus(session.audioMode)}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Latency</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatLatencyMs(latency.latestChunkMs ?? latency.sessionAgeMs ?? metadata.latestChunkLatencyMs)}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  const renderScenarioArtifact = (artifact) => {
    if (!artifact) {
      return null;
    }

    const playbackAudioSrc = artifact.playbackAudioContentBase64
      ? `data:${artifact.playbackMimeType || 'audio/wav'};base64,${artifact.playbackAudioContentBase64}`
      : null;
    const downloadAudioSrc = artifact.audioContentBase64
      ? `data:${artifact.mimeType || 'audio/wav'};base64,${artifact.audioContentBase64}`
      : null;
    const audioSrc = playbackAudioSrc || downloadAudioSrc;

    return (
      <Box sx={{
        mt: 2,
        p: 2,
        bgcolor: '#fffaf0',
        border: '1px solid #f2d49b',
        borderRadius: 1,
        color: '#111',
      }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#111', mb: 1 }}>
          Scenario Result Artifact
        </Typography>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
          mb: audioSrc ? 1 : 0,
        }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">Source transcript</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
              {artifact.sourceTranscript || '-'}
            </Typography>
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary">Translated text</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}>
              {artifact.translatedText || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Language direction</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatLanguage(artifact.detectedSourceLanguage)} to {formatLanguage(artifact.targetLanguage)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Generated audio</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {artifact.audioDurationMs ? `${artifact.audioDurationMs}ms` : '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Export audio</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {artifact.audioEncoding || '-'}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">Artifact type</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatTranslationStatus(artifact.artifactType || 'PROVIDER_TTS_AUDIO')}
            </Typography>
          </Box>
        </Box>
        {artifact.artifactNote && (
          <Alert severity={artifact.artifactType === 'VALIDATION_TONE' ? 'warning' : 'info'} sx={{ mb: 1 }}>
            {artifact.artifactNote}
          </Alert>
        )}
        {audioSrc && (
          <Box>
            <audio controls src={audioSrc} style={{ width: '100%' }}>
              <track kind="captions" />
            </audio>
            {downloadAudioSrc && (
              <Button
                component="a"
                href={downloadAudioSrc}
                download={`scenario-${artifact.targetLanguage || 'audio'}${artifact.mimeType === 'audio/mpeg' ? '.mp3' : '.wav'}`}
                size="small"
                variant="outlined"
                sx={{ mt: 1 }}
              >
                {artifact.artifactType === 'VALIDATION_TONE' ? 'Download validation tone' : 'Download generated voice'}
              </Button>
            )}
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Place a simulated SIP call through the local SIP server to Rhino TAS. Configure caller and callee
        numbers, then trigger a one-click test call.
      </Typography>

      {simulatorStatus && (
        <Alert
          severity={simulatorStatus.callOrigination?.mocked ? 'warning' : 'info'}
          icon={simulatorStatus.callOrigination?.mocked ? <WarningIcon /> : undefined}
          sx={{ mb: 3, maxWidth: 720 }}
        >
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
            <Typography variant="subtitle2" component="span" sx={{ fontWeight: 700 }}>
              {simulatorStatus.callOrigination?.mocked ? 'Simulated call setup enabled' : 'Live call setup enabled'}
            </Typography>
            <Chip
              label={simulatorStatus.simulator?.productionMediaGateway ? 'media-backed' : 'test-call generation only'}
              size="small"
              color={simulatorStatus.simulator?.productionMediaGateway ? 'success' : 'warning'}
              variant="outlined"
            />
            <Chip
              label={simulatorStatus.simulator?.liveAudioProcessed ? 'live audio' : 'no live audio'}
              size="small"
              color={simulatorStatus.simulator?.liveAudioProcessed ? 'success' : 'warning'}
              variant="outlined"
            />
          </Box>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {simulatorStatus.callOrigination?.mocked
              ? 'Calls are acknowledged by the backend simulation path instead of live call origination.'
              : 'Calls use the configured call setup path for this environment.'}
          </Typography>
        </Alert>
      )}

      <Paper sx={{ p: 3, maxWidth: 720 }}>
        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Caller number"
            value={callerNumber}
            onChange={(e) => setCallerNumber(e.target.value)}
            helperText="Use E.164 format (e.g., +66814969080)"
            fullWidth
          />
          <TextField
            label="Callee number"
            value={calleeNumber}
            onChange={(e) => setCalleeNumber(e.target.value)}
            helperText="Destination extension or number (e.g., 6070)"
            fullWidth
          />
          <TextField
            label="Service Tier"
            select
            value={serviceName}
            onChange={(e) => setServiceName(e.target.value)}
            helperText="Select service level (affects rate limiting and policies)"
            fullWidth
          >
            <MenuItem value="default">default (5 calls/min)</MenuItem>
            <MenuItem value="premium">premium (10 calls/min)</MenuItem>
            <MenuItem value="economic">economic (3 calls/min)</MenuItem>
          </TextField>
          <Divider />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            Scenario Harness
          </Typography>
          <TextField
            label="Scenario"
            select
            value={selectedScenarioId}
            onChange={handleScenarioChange}
            helperText={selectedScenario?.expected
              ? `Expected action: ${selectedScenario.expected.finalAction}, translation: ${formatTranslationStatus(selectedScenario.expected.translationStatus)}`
              : 'Select a test scenario'}
            fullWidth
          >
            {scenarios.map((scenario) => (
              <MenuItem key={scenario.id} value={scenario.id}>{scenario.label}</MenuItem>
            ))}
          </TextField>
          <TextField
            label="Scenario provider mode"
            select
            value={scenarioProviderMode}
            onChange={(event) => setScenarioProviderMode(event.target.value)}
            helperText="Fixture mode is deterministic; configured mode uses the selected provider adapters"
            fullWidth
          >
            <MenuItem value="fixture">fixture</MenuItem>
            <MenuItem value="mock">mock</MenuItem>
            <MenuItem value="configured">configured</MenuItem>
          </TextField>
          <TextField
            label="Audio fixture"
            select
            value={scenarioAudioFixtureId}
            onChange={(event) => {
              setScenarioAudioFixtureId(event.target.value);
              setUploadedScenarioAudio(null);
              setAudioUploadLabel('');
            }}
            helperText={audioUploadLabel || `Use a bundled fixture, or upload WAV/MP3 up to ${formatBytes(scenarioLimits.maxAudioBytes)}`}
            fullWidth
          >
            <MenuItem value="">None</MenuItem>
            {audioFixtures.map((fixture) => (
              <MenuItem key={fixture.id} value={fixture.id}>
                {fixture.id} ({fixture.language})
              </MenuItem>
            ))}
          </TextField>
          <Box display="flex" flexWrap="wrap" gap={1}>
            <Button variant="outlined" component="label" disabled={submitting}>
              Upload voice fixture
              <input
                hidden
                type="file"
                accept="audio/wav,audio/wave,audio/x-wav,audio/mpeg,audio/mp3"
                onChange={handleScenarioAudioUpload}
              />
            </Button>
          </Box>
          <Button
            variant="contained"
            startIcon={<PhoneForwardedIcon />}
            onClick={handlePrimaryTestCall}
            disabled={submitting || (scenarios.length > 0 && !selectedScenarioId)}
          >
            {submitting ? 'Running test call...' : 'Run test call'}
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Call Log
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Shows the latest 20 SIP simulator call attempts with detailed trace logs.
        </Typography>
        <Divider sx={{ mb: 2 }} />
        {callLogs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No calls yet.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width="40px"></TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Caller</TableCell>
                <TableCell>Callee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Translation</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Duration</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {callLogs.map((log) => {
                const rhinoSipTrace = getRhinoSipLegTrace(log);
                const rhinoTasTrace = getRhinoTasTrace(log);
                const canExpand = Boolean(log.translationSession) || (log.trace && log.trace.length > 0) || rhinoSipTrace.length > 0 || rhinoTasTrace.length > 0;

                return (
                <React.Fragment key={log.id}>
                  <TableRow 
                    hover 
                    onClick={() => canExpand && toggleExpand(log.id)}
                    sx={{ cursor: canExpand ? 'pointer' : 'default' }}
                  >
                    <TableCell>
                      {canExpand && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleExpand(log.id);
                          }}
                          sx={{
                            transform: expandedLog === log.id ? 'rotate(180deg)' : 'rotate(0deg)',
                            transition: 'transform 0.3s'
                          }}
                        >
                          <ExpandMoreIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                    <TableCell>
                      {log.timestamp.toLocaleTimeString()}
                    </TableCell>
                    <TableCell>{log.callerNumber}</TableCell>
                    <TableCell>{log.calleeNumber}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.status === 'success' ? 'Success' : 'Failed'}
                        color={log.status === 'success' ? 'success' : 'error'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {log.translationSession ? (
                        <Chip
                          label={formatTranslationStatus(log.translationSession.status)}
                          color={getTranslationStatusColor(log.translationSession.status)}
                          size="small"
                        />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell>
                      {log.action && (
                        <Chip
                          label={log.action}
                          color={
                            log.action === 'DROP' ? 'error' : 
                            log.action === 'WHISPER' ? 'warning' : 
                            'success'
                          }
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>{log.duration ? `${log.duration}ms` : '-'}</TableCell>
                  </TableRow>
                  {canExpand && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 0, borderBottom: 'none' }}>
                        <Collapse in={expandedLog === log.id} timeout="auto" unmountOnExit>
                          <Box sx={{ py: 2, px: 2}}>
                            {renderTranslationDetails(log.translationSession)}
                            {renderScenarioArtifact(log.scenarioArtifact)}
                            {log.trace && log.trace.length > 0 && (
                              <>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                  Call Flow Trace ({log.trace.length} steps)
                                </Typography>
                                <List dense>
                                  {log.trace.map((step) => (
                                <ListItem 
                                  key={step.step} 
                                  sx={{ 
                                    py: 1,
                                    borderLeft: '3px solid',
                                    borderColor: step.status === 'success' ? 'success.main' : 
                                               step.status === 'warning' ? 'warning.main' : 
                                               step.status === 'error' ? 'error.main' : 'info.main',
                                    mb: 0.5,
                                    bgcolor: '#1a1a1a'
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 40 }}>
                                    {getStatusIcon(step.status)}
                                  </ListItemIcon>
                                  <Box sx={{ flex: 1 }}>
                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                      <Chip 
                                        label={`Step ${step.step}`} 
                                        size="small" 
                                        variant="outlined"
                                        color={step.status === 'success' ? 'success' : 
                                               step.status === 'warning' ? 'warning' : 
                                               step.status === 'error' ? 'error' : 'info'}
                                        sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
                                      />
                                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#ffffff' }}>
                                        {formatTraceAction(step.action)}
                                      </Typography>
                                      <Typography variant="caption" sx={{ color: '#cccccc', fontStyle: 'italic' }}>
                                        +{step.timestamp}ms
                                      </Typography>
                                     </Box>
                                     <Typography variant="body2" sx={{ color: '#ffffff', mb: step.details ? 0.5 : 0 }}>
                                      {formatDisplayValue(step.message, 'Event logged')}
                                     </Typography>
                                    {step.details && Object.keys(step.details).length > 0 && (
                                      <Box sx={{ 
                                        mt: 0.5, 
                                        pl: 2, 
                                        py: 0.5, 
                                        bgcolor: '#ffffff', 
                                        borderRadius: 1,
                                        border: '1px solid #cccccc',
                                        '& div': {
                                          color: '#000000 !important'
                                        },
                                        '& strong': {
                                          color: '#000000 !important'
                                        },
                                        '& span': {
                                          color: '#000000 !important'
                                        }
                                      }}>
                                        {Object.entries(step.details).map(([key, value]) => (
                                          <div 
                                            key={key}
                                            style={{ 
                                              fontFamily: 'monospace',
                                              fontWeight: 500,
                                              fontSize: '0.75rem',
                                              display: 'block',
                                              margin: '2px 0'
                                            }}
                                          >
                                            <strong>{key}:</strong> <span>{formatDisplayValue(value, '-')}</span>
                                          </div>
                                        ))}
                                      </Box>
                                    )}
                                  </Box>
                                </ListItem>
                                  ))}
                                </List>
                              </>
                            )}
                            {log.scammerCheck && (
                              <Box sx={{ 
                                mt: 2, 
                                p: 2, 
                                bgcolor: log.scammerCheck.isScammer ? '#ffebee' : '#e8f5e9', 
                                border: '2px solid',
                                borderColor: log.scammerCheck.isScammer ? 'error.main' : 'success.main',
                                borderRadius: 1 
                              }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: '#787878' }}>
                                  🔍 Scammer Detection Result
                                </Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 1, color: '#000000' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Scammer:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{log.scammerCheck.isScammer ? '⚠️ Yes' : '✓ No'}</Typography>
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Confidence:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{(log.scammerCheck.confidence * 100).toFixed(0)}%</Typography>
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Fraud Score:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{log.scammerCheck.fraudScore || 0}/100</Typography>
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Carrier:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{log.scammerCheck.carrier || 'N/A'}</Typography>
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Category:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{log.scammerCheck.category}</Typography>
                                  
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#000000' }}>Reason:</Typography>
                                  <Typography variant="body2" sx={{ color: '#000000' }}>{log.scammerCheck.reason}</Typography>
                                </Box>
                              </Box>
                            )}
                            {rhinoSipTrace.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                  📡 Rhino ↔ SIP Server Trace Log ({rhinoSipTrace.length} events)
                                </Typography>

                                <Box sx={{ border: '1px solid #d9e8ff', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                                  <Box sx={{
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: '#eef5ff',
                                    borderBottom: '1px solid #d9e8ff',
                                    display: 'grid',
                                    gridTemplateColumns: '130px 180px 95px 170px 1fr',
                                    gap: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#1b3f6f'
                                  }}>
                                    <Box>Time (ms)</Box>
                                    <Box>Direction</Box>
                                    <Box>Protocol</Box>
                                    <Box>Event</Box>
                                    <Box>Details</Box>
                                  </Box>

                                  {rhinoSipTrace.map((evt, index) => (
                                    <Box
                                      key={`${log.id}-${evt.source}-${evt.event}-${index}`}
                                      sx={{
                                        px: 1.5,
                                        py: 0.8,
                                        borderBottom: index === rhinoSipTrace.length - 1 ? 'none' : '1px solid #f0f4fb',
                                        display: 'grid',
                                        gridTemplateColumns: '130px 180px 95px 170px 1fr',
                                        gap: 1,
                                        fontSize: '0.78rem',
                                        color: '#111'
                                      }}
                                    >
                                      <Box sx={{ color: '#4b6b90', fontFamily: 'monospace', textAlign: 'right' }}>
                                        {typeof evt.relativeTime === 'number' ? `+${evt.relativeTime.toFixed(2)} ms` : '-'}
                                      </Box>
                                      <Box>{evt.direction}</Box>
                                      <Box sx={{ color: '#2f5a88' }}>{evt.protocol}</Box>
                                      <Box sx={{ fontFamily: 'monospace' }}>{evt.event}</Box>
                                      <Box>
                                        <Box sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', mb: 0.3 }}>
                                          {evt.sipMessage || evt.details}
                                        </Box>
                                        <Box sx={{ fontSize: '0.75rem', color: '#888' }}>
                                          {evt.sipMessage && evt.details !== evt.sipMessage ? evt.details : ''}
                                        </Box>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                            {rhinoTasTrace.length > 0 && (
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                                    📜 Rhino TAS Full Trace ({rhinoTasTrace.length} events)
                                  </Typography>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => copyRhinoTasTrace(log)}
                                    sx={{ mb: 0.5 }}
                                  >
                                    Copy JSON
                                  </Button>
                                </Box>

                                <Box sx={{ border: '1px solid #e7e7e7', borderRadius: 1, overflow: 'hidden', bgcolor: '#fff' }}>
                                  <Box sx={{
                                    px: 1.5,
                                    py: 1,
                                    bgcolor: '#f6f6f6',
                                    borderBottom: '1px solid #e7e7e7',
                                    display: 'grid',
                                    gridTemplateColumns: '120px 190px 110px 1fr',
                                    gap: 1,
                                    fontSize: '0.75rem',
                                    fontWeight: 700,
                                    color: '#3b3b3b'
                                  }}>
                                    <Box>Time (ms)</Box>
                                    <Box>Event</Box>
                                    <Box>Status</Box>
                                    <Box>Details</Box>
                                  </Box>

                                  {rhinoTasTrace.map((evt) => (
                                    <Box
                                      key={evt.id}
                                      sx={{
                                        px: 1.5,
                                        py: 0.8,
                                        borderBottom: '1px solid #f1f1f1',
                                        display: 'grid',
                                        gridTemplateColumns: '120px 190px 110px 1fr',
                                        gap: 1,
                                        fontSize: '0.78rem',
                                        color: '#111'
                                      }}
                                    >
                                      <Box sx={{ color: '#4b6b90', fontFamily: 'monospace', textAlign: 'right' }}>
                                        {typeof evt.time === 'number' ? `+${evt.time.toFixed(2)} ms` : '-'}
                                      </Box>
                                      <Box sx={{ fontFamily: 'monospace' }}>{evt.event}</Box>
                                      <Box>{evt.status}</Box>
                                      <Box>
                                        <Box sx={{ fontSize: '0.8rem', fontWeight: 600, color: '#555', mb: 0.3 }}>
                                          {evt.details}
                                        </Box>
                                        <Box sx={{ fontSize: '0.75rem', color: '#888' }}>
                                          {evt.payload}
                                        </Box>
                                      </Box>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Snackbar open={notice.open} autoHideDuration={4000} onClose={handleClose}>
        <Alert onClose={handleClose} severity={notice.severity} variant="filled">
          {notice.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

